import { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchProductionModels, fetchProductionModelData, refreshProductionModels } from '@/lib/production-database-api'
import type { ModelInfo, ModelDataResponse, ModelDataOptions } from '@/types/database'

export const productionDatabaseKeys = {
  all: ['production-database'] as const,
  models: (projectId: number) => [...productionDatabaseKeys.all, 'models', projectId] as const,
  modelData: (projectId: number, appLabel: string, modelName: string, options: ModelDataOptions) =>
    [...productionDatabaseKeys.all, 'data', projectId, appLabel, modelName, options] as const,
}

interface UseProductionModelsResult {
  data: { models: ModelInfo[]; total_count: number } | undefined
  isLoading: boolean
  error: Error | null
  isRefreshing: boolean
  refresh: () => void
}

export function useProductionModels(projectId: number, enabled = true): UseProductionModelsResult {
  const queryClient = useQueryClient()
  const [isRefreshing, setIsRefreshing] = useState(false)

  const query = useQuery({
    queryKey: productionDatabaseKeys.models(projectId),
    queryFn: () => fetchProductionModels(projectId),
    enabled,
    staleTime: 10 * 60 * 1000,
  })

  const refresh = useCallback(async () => {
    setIsRefreshing(true)
    try {
      const freshData = await refreshProductionModels(projectId)
      queryClient.setQueryData(productionDatabaseKeys.models(projectId), freshData)
    } catch {
      await query.refetch()
    } finally {
      setIsRefreshing(false)
    }
  }, [projectId, queryClient, query])

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isRefreshing,
    refresh,
  }
}

interface UseProductionModelDataResult {
  data: ModelDataResponse | undefined
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => void
}

export function useProductionModelData(
  projectId: number,
  appLabel: string | null,
  modelName: string | null,
  options: ModelDataOptions = {}
): UseProductionModelDataResult {
  const enabled = !!appLabel && !!modelName

  const query = useQuery({
    queryKey: enabled
      ? productionDatabaseKeys.modelData(projectId, appLabel!, modelName!, options)
      : ['disabled'],
    queryFn: () => fetchProductionModelData(projectId, appLabel!, modelName!, options),
    enabled,
    staleTime: 3 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  }
}
